/**
 * 掲載中のメニュー 1 品 — いまの掲載値・出どころ・推定の中身と、公式値の入力への入口。
 *
 * 推定の中身（根拠・信頼度・推定した食材）は kalori.jp の商品ページでも公開しているものと同じ範囲
 * （`src/domain/catalog-view.ts` 線 2）。一番直してほしい部分なので隠さない。
 */
import { useEffect, useState } from "react";
import { api, type CatalogItem, type MenuEntry } from "../api";
import { Link, navigate } from "../router";
import { NUTRITION_FIELDS, REVIEW_STATE_LABEL, fmtDateTime, fmtNumber, fmtYen } from "../format";
import { SourceBadge } from "./parts";
import { useFeedback } from "./Feedback";

export function Product({ productId }: { productId: number }) {
  const [item, setItem] = useState<CatalogItem | null | undefined>(undefined);
  const [entry, setEntry] = useState<MenuEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { open: openFeedback } = useFeedback();

  useEffect(() => {
    let alive = true;
    void Promise.all([api.catalogItem(productId), api.entries()]).then(([p, e]) => {
      if (!alive) return;
      if (p.ok) setItem(p.data);
      else {
        setItem(null);
        setError(p.error);
      }
      if (e.ok) setEntry(e.data.find((x) => x.productDbId === productId) ?? null);
    });
    return () => {
      alive = false;
    };
  }, [productId]);

  /**
   * 台帳に行を作ってこの商品に紐付け、入力画面へ。
   * ⚠️ 入力欄はいまの掲載値で埋めない — AI 推定をそのまま「御社の公式値」として申請できてしまうため。
   *    いまの値は入力画面で横に「参考」として並べる。
   */
  const start = async () => {
    if (!item) return;
    setBusy(true);
    setError(null);
    const created = await api.createEntry({ name: item.name });
    if (!created.ok) {
      setBusy(false);
      return setError(created.error);
    }
    const linked = await api.linkEntry(created.data.id, item.productId);
    if (!linked.ok) {
      await api.deleteEntry(created.data.id);
      setBusy(false);
      return setError(linked.error);
    }
    navigate(`/entries/${created.data.id}`);
  };

  if (item === undefined) return <p className="muted">読み込んでいます…</p>;
  if (item === null)
    return (
      <section className="stack">
        <p className="error">{error ?? "メニューが見つかりません。"}</p>
        <Link href="/catalog">掲載中のメニューへ戻る</Link>
      </section>
    );

  return (
    <section className="stack-lg">
      <div>
        <p className="small">
          <Link href="/catalog">← 掲載中のメニュー</Link>
        </p>
        <h1>{item.name}</h1>
        <p className="muted">
          {fmtYen(item.price)}
          {item.sourcePageUrl && (
            <>
              {" ・ "}
              <a href={item.sourcePageUrl} target="_blank" rel="noopener noreferrer">
                御社サイトの掲載ページ
              </a>
            </>
          )}
          {item.lastConfirmedAt && ` ・ 公式サイトからの最終取得 ${fmtDateTime(item.lastConfirmedAt)}`}
          {" ・ "}
          {/* 栄養値以外（終売・価格・名前・写真）の誤りはここから。栄養値は公式値の入力で直す */}
          <button
            type="button"
            className="btn-link"
            onClick={() => openFeedback({ category: "listing", product: { id: item.productId, name: item.name } })}
          >
            このメニューについて運営に伝える
          </button>
        </p>
      </div>

      {item.attention && (
        <div className="notice">
          <strong>確認をおすすめします</strong>
          <div>
            掲載中のエネルギー {fmtNumber(item.nutrition.cal, "kcal")} に対し、たんぱく質・脂質・炭水化物から計算すると{" "}
            {fmtNumber(item.attention.pfcDerivedCal, "kcal")}（4×P + 9×F + 4×C）で、30% 以上ずれています。どれかの値が誤っている可能性が高い品です。
          </div>
          <div className="small">
            出どころが「AI 推定」「一部推定」の項目があれば、その値の公式値を入力いただくと直ります。御社サイトの値どうしがずれている場合は、サイトの表記もご確認ください。
          </div>
        </div>
      )}

      {item.officialRequests ? (
        <p className="notice">
          このメニューの公式値を <strong>{item.officialRequests.toLocaleString("ja-JP")} 件</strong>{" "}
          希望されています（kalori のアプリと kalori.jp で「このお店に公式値を希望する」が押された件数）。
        </p>
      ) : null}

      <div className="card">
        <div className="row-between">
          <h2>いまの掲載値</h2>
          {entry ? (
            <Link href={`/entries/${entry.id}`} className="btn primary">
              入力中の公式値を開く（{REVIEW_STATE_LABEL[entry.reviewState]}）
            </Link>
          ) : (
            <button className="btn primary" disabled={busy} onClick={() => void start()}>
              {busy ? "準備しています…" : "公式値を入力する"}
            </button>
          )}
        </div>
        {error && <p className="error">{error}</p>}
        <table className="table kv">
          <tbody>
            {NUTRITION_FIELDS.map((f) => (
              <tr key={f.key}>
                <th>{f.label}</th>
                <td className="num">{fmtNumber(item.nutrition[f.key], f.unit)}</td>
                <td>
                  <SourceBadge source={item.provenance[f.key]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {item.estimation && (
        <div className="card">
          <h2>kalori の推定について</h2>
          <p className="muted small">
            御社サイトに値が無い項目は、写真や原材料から kalori が推定しています（kalori.jp の商品ページにも表示しています）。
          </p>
          <dl className="dl">
            {item.estimation.recognizedFood && (
              <>
                <dt>推定した料理</dt>
                <dd>{item.estimation.recognizedFood}</dd>
              </>
            )}
            {item.estimation.confidence != null && (
              <>
                <dt>信頼度</dt>
                <dd>{Math.round(item.estimation.confidence * 100)}%</dd>
              </>
            )}
            {item.estimation.reasoning && (
              <>
                <dt>推定の根拠</dt>
                <dd className="pre">{item.estimation.reasoning}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </section>
  );
}
